import { Grid, Paper } from '@mui/material';
import EditWallToolbar from './EditWallToolbar';
import { useEffect, useState } from 'react';
import { Coordinates, WeatherWallGrid } from './types';
import WeatherWindowDialog from './WeatherWindowDialog';
import WeatherWindow from './WeatherWindow';
import { useLocation } from 'react-router-dom';
import { useWeatherWallMaxColumns } from './hooks/useWeatherWallMaxColumns';
import { useWeatherWall } from './api/getWeatherWall';

const WeatherWallPage = () => {
  const pathName = useLocation().pathname;
  const inEditMode = pathName.includes('edit');
  const splitPath = pathName.split('/');
  const wallId = Number(splitPath[splitPath.length - 1]);
  const { data: wall } = useWeatherWall({ wallId });
  const wallMaxColumns = useWeatherWallMaxColumns();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [clickedCoordinates, setClickedCoordinates] = useState<Coordinates>({
    x: -1,
    y: -1,
  });
  const [currentGridWithChanges, setCurrentGridWithChanges] =
    useState<WeatherWallGrid>({
      name: wall?.name || '',
      rows: wall?.rows || 1,
      columns: wall?.columns || 1,
    });

  // Reset component grid state whenever wall in Redux store/DB changes or we enter/exit edit mode
  useEffect(() => {
    if (wall)
      setCurrentGridWithChanges({
        name: wall.name,
        rows: wall.rows,
        columns: wall.columns,
      });
  }, [wall, inEditMode]);

  // Prevent edit dialog from popping open upon entering edit mode
  useEffect(() => {
    setEditDialogOpen(false);
  }, [inEditMode]);

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setClickedCoordinates({ x: -1, y: -1 });
  };
  const handleWindowClicked = (rowIndex: number, columnIndex: number) => {
    setClickedCoordinates({ x: rowIndex, y: columnIndex });
    if (!inEditMode) {
      const foundWindow = wall?.weatherWindows.find(
        (window) =>
          window.pos_x === rowIndex &&
          window.pos_y === columnIndex &&
          window.weather_wall_id === wall?.id
      );
      if (!foundWindow) return;
    } else {
      setEditDialogOpen(true);
    }
  };
  const calcWallSize = () => {
    if (Math.min(wallMaxColumns, currentGridWithChanges.columns) === 1) {
      return 12;
    } else if (Math.min(wallMaxColumns, currentGridWithChanges.columns) === 2) {
      return 6;
    } else if (Math.min(wallMaxColumns, currentGridWithChanges.columns) === 3) {
      return 4;
    } else if (Math.min(wallMaxColumns, currentGridWithChanges.columns) === 4) {
      return 3;
    }
  };
  if (!wall) {
    return null;
  }
  if (!inEditMode && wallMaxColumns < wall.columns)
    return (
      <div style={{ paddingBottom: '1.5rem' }}>
        <Grid container>
          {[...Array(wall.rows)].map((row, rowIndex) =>
            [...Array(wall.columns)].map((column, columnIndex) => (
              <Grid
                item
                key={columnIndex}
                xs={calcWallSize()}
                component={Paper}
                style={{ position: 'relative' }}
                onClick={() => handleWindowClicked(rowIndex, columnIndex)}
              >
                <WeatherWindow
                  row={rowIndex}
                  column={columnIndex}
                  wallId={wallId}
                />
              </Grid>
            ))
          )}
        </Grid>
      </div>
    );
  else
    return (
      <div style={{ paddingBottom: '1.5rem' }}>
        {inEditMode && (
          <>
            <EditWallToolbar
              currentGridWithChanges={currentGridWithChanges}
              setCurrentGridWithChanges={setCurrentGridWithChanges}
              wall={wall}
            />
            <WeatherWindowDialog
              open={editDialogOpen}
              handleClose={handleCloseEditDialog}
              wallId={wall.id}
              xPos={clickedCoordinates.x}
              yPos={clickedCoordinates.y}
            />
          </>
        )}
        {[...Array(currentGridWithChanges.rows)].map((row, rowIndex) => (
          <Grid container wrap='nowrap' key={rowIndex}>
            {[...Array(currentGridWithChanges.columns)].map(
              (column, columnIndex) => (
                <Grid
                  item
                  key={columnIndex}
                  xs={calcWallSize()}
                  component={Paper}
                  style={{ position: 'relative' }}
                  onClick={() => handleWindowClicked(rowIndex, columnIndex)}
                >
                  <WeatherWindow
                    row={rowIndex}
                    column={columnIndex}
                    wallId={wallId}
                  />
                </Grid>
              )
            )}
          </Grid>
        ))}
      </div>
    );
};

export default WeatherWallPage;
